import type { ImagePageLoader } from "~/src/reader/managers/cdnImage";
import type { ChapterEntity } from "~/src/api";
import { nanoid } from "nanoid";

export type PageGroupTypes =
  | ImagePageGroup
  | InterludePageGroup
  | SubscribeLockedPageGroup
  | PlaceholderPageGroup;

export class ImagePageGroup {
  id = nanoid();
  error?: Error;
  width: number;
  height: number;

  constructor(
    public chapterId: string,
    public pages: ImagePageLoader[],
  ) {
    const [width, height] = this.calculateCombinedDimensions();
    this.width = width;
    this.height = height;
  }

  async loadPages(force = false) {
    this.error = undefined;
    this.error = await this.loadAllPages(force);
    if (this.error) return null;
  }

  destroy(propagatePages = false) {
    if (propagatePages) this.pages.forEach((page) => page.unload());
  }

  getProgress() {
    return (
      this.pages.reduce((prev, curr) => prev + curr.progress, 0) /
      this.pages.length
    );
  }

  private async loadAllPages(force = false) {
    await Promise.all(this.pages.map((page) => page.load(force)));
    if (this.pages.some((page) => page.error))
      return new Error("Failed to load pages in group");
  }

  private calculateCombinedDimensions(): [number, number] {
    const width = this.pages.reduce((acc, page) => acc + page.width, 0);
    const height = Math.max(...this.pages.map((page) => page.height));

    return [width, height];
  }
}

export class InterludePageGroup {
  chapterId: string;
  id = nanoid();
  type: "end" | "middle" | "start";
  nextChapterId: string | null;
  prevChapterId: string | null;

  constructor(options: {
    chapterId: string;
    type: "end" | "middle" | "start";
    nextChapterId: string | null;
    prevChapterId: string | null;
  }) {
    this.chapterId = options.chapterId;
    this.type = options.type;
    this.nextChapterId = options.nextChapterId;
    this.prevChapterId = options.prevChapterId;
  }
}

export class SubscribeLockedPageGroup {
  id = nanoid();
  constructor(
    public chapterId: string,
    public chapter?: ChapterEntity,
  ) {}
}

export class PlaceholderPageGroup {
  id = "placeholder";
  width: number;
  height: number;
  constructor(
    public chapterId: string,
    width?: number,
    height?: number,
  ) {
    this.chapterId = chapterId;
    this.width = width ?? 1200;
    this.height = height ?? 1600;
  }
}
